import { useState } from 'react'

import Button from 'common/components/button'
import ConfirmationModal from 'common/components/confirmationModal/ConfirmationModal'
import Drawer from 'common/components/drawer'
import EmptyState from 'common/components/emptyState'
import SingleSelect from 'common/components/singleSelect'
import { successToast } from 'common/components/toastNotification'
import TableWidget from 'common/components/widget/tableWidget'
import { formatIsoDate } from 'common/helpers/date'
import { useWindowDimensions } from 'common/hooks/useWindowDimensions'
import { Bin as DeleteIcon } from 'common/icons'

import { CustomerAttributesResponse } from '../api'
import useDeleteCustomerAttributeRecordMutation from '../api/mutations/useDeleteCustomerAttributeRecordMutation'
import useUpdateCustomerAttributesMutation from '../api/mutations/useUpdateCustomerAttributesMutation'
import { CustomerAttributeRecord } from '../api/queries/useGetCustomerAttributeRecordsQuery'

import EditAttributeModal from './EditAttributeModal'
import ExplainAttributesModal from './ExplainAttributesModal'
import { useCustomerAttributesColumns } from './useCustomerAttributesColumns'

type CustomerAttributesColumn = CustomerAttributesResponse

type Props = {
  onAddNewRecord: () => void
  onChangeRecord: (value: string | undefined) => void
  selectedRecordId: string | undefined
  records: CustomerAttributeRecord[]
  attributes: CustomerAttributesColumn[]
  isLoading: boolean
}

const CustomerAttributesTable = ({
  onAddNewRecord,
  onChangeRecord,
  selectedRecordId,
  records,
  attributes,
  isLoading,
}: Props) => {
  const [resetTableWidgetKey, setResetTableWidgetKey] = useState(0)

  const [draftDescription, setDraftDescription] = useState<string>('')
  const [attributeToEdit, setAttributeToEdit] = useState<
    CustomerAttributesColumn | undefined
  >(undefined)
  const [isEditAttributeModalOpened, setIsEditAttributeModalOpened] =
    useState(false)
  const [isExplainModalVisible, setIsExplainModalVisible] = useState(false)

  const [selectedAttribute, setSelectedAttribute] = useState<
    CustomerAttributesColumn | undefined
  >(undefined)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const selectedRecord = records.find(
    (record) => record.id === selectedRecordId
  )

  const { mutate: updateAttribute, isPending: isUpdatingAttribute } =
    useUpdateCustomerAttributesMutation()

  const { mutate: deleteRecord, isPending: isDeleting } =
    useDeleteCustomerAttributeRecordMutation()

  const { screenWidth } = useWindowDimensions()

  const handleUpdateAttribute = (params: {
    attributeId: string
    catalogueAttributeId: string
    description: string
    region: string
    isUsed: boolean
    isCmabUsed: boolean
  }) => {
    updateAttribute(params, {
      onSuccess: () => {
        setIsEditAttributeModalOpened(false)
        successToast('Attribute updated successfully')
      },
    })
  }

  const columns = useCustomerAttributesColumns({
    screenWidth,
    selectedRecord,
    updateAttribute: handleUpdateAttribute,
    setAttributeToEdit,
    setIsEditAttributeModalOpened,
    setDraftDescription,
  })

  return (
    <div>
      <TableWidget.Widget
        key={`${resetTableWidgetKey}`}
        columns={columns}
        data={attributes}
        isLoading={isLoading}
        className="mt-6"
        rowClassName="bg-coolGray-50"
        onRowClick={setSelectedAttribute}
        firstUseText={
          <div className="p-6">
            <EmptyState title="Awaiting data">
              <a
                href="https://support.jacquard.com/en/articles/10223324-personalised-campaigns-getting-started"
                className="underline uppercase text-gold-600 hover:text-gold-800"
                target="_blank"
                rel="noreferrer"
              >
                Getting started guide
              </a>
            </EmptyState>
          </div>
        }
      >
        <TableWidget.Header
          title={
            <div className="mb-4 flex gap-6 text-gold-500 text-xs items-center">
              <SingleSelect
                className="max-w-50"
                onChange={(value) => {
                  onChangeRecord(value?.value)
                  setResetTableWidgetKey((prev) => prev + 1)
                }}
                value={selectedRecordId}
                isDisabled={records.length === 1}
                options={records.map((record) => ({
                  label: record.name,
                  value: record.id,
                }))}
              />
              <div>Region: {selectedRecord?.region}</div>
              <div>Last update: {formatIsoDate(selectedRecord?.updated)}</div>
            </div>
          }
        >
          <div className="flex gap-4">
            {selectedRecord && (
              <Button
                data-cy="delete-customer-attribute-button"
                data-testid="delete-customer-attribute-button"
                variant="icon"
                prefixIcon={<DeleteIcon />}
                onClick={() => setIsDeleteModalOpen(true)}
              />
            )}
            <Button
              data-cy="add-incompatible-word-button"
              data-testid="add-incompatible-word-button"
              variant="primary"
              onClick={() => {
                onAddNewRecord()
                setResetTableWidgetKey((prev) => prev + 1)
              }}
            >
              Add new record
            </Button>
            <TableWidget.Filter />
          </div>
        </TableWidget.Header>
      </TableWidget.Widget>
      <Drawer
        title={selectedRecord?.name ?? ''}
        isOpen={selectedAttribute !== undefined}
        isPush={true}
        content={
          <div className="mt-6 flex flex-col gap-4 flex-nowrap">
            <div className="text-base-700 text-6 font-medium capitalize-first">
              {selectedAttribute?.name}
            </div>
            <div className="whitespace-pre-wrap break-words w-82">
              {selectedAttribute?.description}
            </div>
          </div>
        }
        onOpenChange={() => setSelectedAttribute(undefined)}
        container={document.getElementById('test')}
      />
      {attributeToEdit && (
        <EditAttributeModal
          attribute={attributeToEdit}
          description={draftDescription}
          onChangeDescription={setDraftDescription}
          isSaving={isUpdatingAttribute}
          onSave={({ description }) => {
            updateAttribute(
              {
                attributeId: attributeToEdit.id,
                catalogueAttributeId: selectedRecord?.id ?? '',
                description,
                region: selectedRecord?.region ?? '',
                isUsed: attributeToEdit.isUsed,
                isCmabUsed: attributeToEdit.isCmabUsed,
              },
              {
                onSuccess: () => {
                  setIsEditAttributeModalOpened(false)
                  successToast('Attribute updated successfully')
                },
              }
            )
          }}
          isOpened={isEditAttributeModalOpened}
          onClose={() => setIsEditAttributeModalOpened(false)}
          onClickNeedHelp={() => {
            setIsExplainModalVisible(true)
            setIsEditAttributeModalOpened(false)
          }}
        />
      )}
      <ExplainAttributesModal
        isOpened={isExplainModalVisible}
        onClose={() => {
          setIsExplainModalVisible(false)
          setIsEditAttributeModalOpened(true)
        }}
      />
      <ConfirmationModal
        title="Delete customer attribute record"
        confirmationText={`Are you sure you want to delete the "${selectedRecord?.name}" record? This action cannot be undone.`}
        open={isDeleteModalOpen}
        isLoading={isDeleting}
        onConfirm={() => {
          if (selectedRecord) {
            deleteRecord(
              {
                catalogueAttributeId: selectedRecord.id,
                region: selectedRecord.region,
              },
              {
                onSuccess: () => {
                  setIsDeleteModalOpen(false)
                  onChangeRecord(records.length > 1 ? records[0].id : undefined)
                },
              }
            )
          }
        }}
        onCancel={() => setIsDeleteModalOpen(false)}
      />
    </div>
  )
}

export default CustomerAttributesTable
